import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    IBACPredesign,
    TDefaultPredesignValues,
    TGreetingCardDesign,
    VALID_IMAGE_DESIGN_TYPES,
} from '@/src/modules/build-a-card/types'
import {
    CARRIER_FONT_STANDARD,
    CARRIER_TYPE_STANDARD,
    FORM_FIELDS_WRAPPER_ID,
} from '@/src/modules/build-a-card/constants'
import getDefaultDenom from '@/src/common/utilities/getDefaultDenom'
import determineDefaultImageValues from '@/src/modules/build-a-card/utilities/determineDefaultImageValues'
import { useRouter } from 'next/router'
import {
    BRAND_PAGE,
    PG_PLUS_BUILD_PAGE,
    VIRTUAL_VISA_PAGE,
} from '@/src/modules/pg-plus/constants'
import {
    TPartnerPayload,
    TWhiteLabelIdentifiers,
} from '@/src/modules/partners/types'
import { scrollToTop } from '@/src/common/utilities/scrollToTop'

// this context handles the virtual visa and branded pg+ purchase flowsx

type TBasicGiftCardContext = {
    predesigns: IBACPredesign[]
    defaultImage: string
    defaultToPredesign: boolean
    currentStep: number
    setCurrentStep: Function
    denomination: string
    setDenomination: Function
    predesignUuid: string
    setPredesignUuid: Function
    customDesignObject: object
    setCustomDesignObject: Function
    designImage: string
    setDesignImage: Function
    designName: string
    setDesignName: Function
    senderName: string
    setSenderName: Function
    recipientName: string
    setRecipientName: Function
    recipientMessage: string
    setRecipientMessage: Function
    nameOnCard: string
    setNameOnCard: Function
    messageOnCard: string
    setMessageOnCard: Function
    greetingCards: TGreetingCardDesign[]
    greetingCardUuid: string
    setGreetingCardUuid: Function
    carrierType: string
    setCarrierType: Function
    fontSelection: string
    setFontSelection: Function
    handwrittenEnabled: boolean
    hasGreetingCard: boolean
    handwrittenFontClassName: string
    selectedImageType: string
    setSelectedImageType: Function
    greetingCardDesignName: string
    setGreetingCardDesignName: Function
    defaultPredesign: TDefaultPredesignValues
    partnerData?: TPartnerPayload
    whiteLabelIdentifiers?: TWhiteLabelIdentifiers
}

export const BasicGiftCardContext = createContext<TBasicGiftCardContext | null>(
    null
)

export const useBasicGiftCardContext = (): TBasicGiftCardContext => {
    const context = useContext(BasicGiftCardContext)

    if (!context) {
        throw new Error('Please use BasicGiftCardContext in parent component')
    }

    return context
}

type TBasicGiftProviderProps = {
    predesigns: IBACPredesign[]
    children: ReactNode
    defaultImage?: string
    greetingCards?: TGreetingCardDesign[]
    defaultToPredesign?: boolean
    handwrittenEnabled?: boolean
    handwrittenFontClassName?: string
    partnerData?: TPartnerPayload
    whiteLabelIdentifiers?: TWhiteLabelIdentifiers
    defaultDenomination?: number
}

// is used in Plastic, Virtual, Closed-Loop gift purchase flows
export const BasicGiftCardProvider = ({
    predesigns,
    children,
    defaultImage = 'https://res.cloudinary.com/gift-card-granny/image/upload/v1668183619/PGSite/product_images/bac_placeholder_j0gpba.png',
    // defaultToPredesign is our EXISTING way of set/show a predesign + wrapper
    defaultToPredesign = true,
    // defaultPredesign is our NEW way of setting a default predesign WITHOUT showing the wrapper
    greetingCards = [],
    handwrittenEnabled = true,
    handwrittenFontClassName = '',
    partnerData,
    whiteLabelIdentifiers,
    defaultDenomination = 100,
}: TBasicGiftProviderProps) => {
    const [currentStep, setCurrentStep] = useState(1)
    const [denomination, setDenomination] = useState(
        getDefaultDenom(defaultDenomination)
    )

    // used to determine default predesign values
    const router = useRouter()
    const defaultPredesign = determineDefaultImageValues(router, predesigns)
    const { default_image_uuid, default_design_name, default_preview_image } =
        defaultPredesign

    const [predesignUuid, setPredesignUuid] = useState(default_image_uuid)
    const [designImage, setDesignImage] = useState(default_preview_image)
    const [designName, setDesignName] = useState<string>(default_design_name)
    const [customDesignObject, setCustomDesignObject] = useState({})

    // used for handling the active state of selected image button either predesign, custom, or default
    const [selectedImageType, setSelectedImageType] =
        useState<VALID_IMAGE_DESIGN_TYPES>('')

    const [senderName, setSenderName] = useState('')
    const [recipientName, setRecipientName] = useState<string>('')
    const [recipientMessage, setRecipientMessage] = useState('')
    const [nameOnCard, setNameOnCard] = useState('')
    const [messageOnCard, setMessageOnCard] = useState('')
    const [carrierType, setCarrierType] = useState<string>('')
    const [fontSelection, setFontSelection] = useState<string>(
        CARRIER_FONT_STANDARD
    )

    // handles greeting card data
    const isStandardCarrier: boolean = carrierType === CARRIER_TYPE_STANDARD
    const [greetingCardUuid, setGreetingCardUuid] = useState<string>(
        isStandardCarrier ? '' : greetingCards?.[0]?.uuid
    )
    const [greetingCardDesignName, setGreetingCardDesignName] =
        useState<string>(
            isStandardCarrier ? '' : greetingCards?.[0]?.designName
        )
    const hasGreetingCard: boolean = !isStandardCarrier && !!greetingCardUuid

    // only apply default predesign images to pg+ flow
    // once we apply to all flows, the valid flow check can be removed
    // isValidPage check located in determineDefaultImageValues
    useEffect(() => {
        if (
            [BRAND_PAGE, PG_PLUS_BUILD_PAGE, VIRTUAL_VISA_PAGE].includes(
                router?.pathname
            )
        ) {
            setDesignImage(default_preview_image)
            setDesignName(default_design_name)
            setPredesignUuid(default_image_uuid)
        }
    }, [
        default_design_name,
        default_image_uuid,
        default_preview_image,
        router?.pathname,
    ])

    useEffect(() => {
        if (currentStep > 1) {
            scrollToTop(FORM_FIELDS_WRAPPER_ID)
        }
    }, [currentStep])

    const functions = useMemo(
        () => ({
            setSenderName,
            setNameOnCard,
            setMessageOnCard,
            setCurrentStep,
            setGreetingCardUuid,
            setCarrierType,
            setFontSelection,
            setDenomination,
            setPredesignUuid,
            setCustomDesignObject,
            setDesignImage,
            setDesignName,
            setRecipientMessage,
            setRecipientName,
            setSelectedImageType,
            setGreetingCardDesignName,
        }),
        []
    )

    const values = useMemo(
        () => ({
            denomination,
            predesignUuid,
            customDesignObject,
            designImage,
            designName,
            senderName,
            recipientName,
            recipientMessage,
            predesigns,
            defaultImage,
            defaultToPredesign,
            nameOnCard,
            messageOnCard,
            currentStep,
            greetingCards,
            greetingCardUuid,
            carrierType,
            fontSelection,
            handwrittenEnabled,
            hasGreetingCard,
            handwrittenFontClassName,
            selectedImageType,
            greetingCardDesignName,
            defaultPredesign,
            partnerData,
            whiteLabelIdentifiers,
        }),
        [
            denomination,
            predesignUuid,
            customDesignObject,
            designImage,
            designName,
            senderName,
            recipientName,
            recipientMessage,
            predesigns,
            defaultImage,
            defaultToPredesign,
            nameOnCard,
            messageOnCard,
            currentStep,
            greetingCards,
            greetingCardUuid,
            carrierType,
            fontSelection,
            handwrittenEnabled,
            hasGreetingCard,
            handwrittenFontClassName,
            selectedImageType,
            greetingCardDesignName,
            defaultPredesign,
            partnerData,
            whiteLabelIdentifiers,
        ]
    )

    return (
        <BasicGiftCardContext.Provider value={{ ...values, ...functions }}>
            {children}
        </BasicGiftCardContext.Provider>
    )
}
